import { useState, useRef, useEffect } from "react";
import clsx from "clsx";

interface MultiSelectInputProps {
  label: React.ReactNode;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  isSaving: boolean;
  rightElement?: React.ReactNode;
}

export default function MultiSelectInput({
  label,
  options,
  selected,
  onChange,
  isSaving,
  rightElement,
}: MultiSelectInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(
    (opt) =>
      opt.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selected.includes(opt)
  );

  const handleAdd = (value: string) => {
    if (!selected.includes(value)) {
      onChange([...selected, value]);
      setInputValue("");
    }
  };

  const handleRemove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mb-4 relative w-full" ref={containerRef}>
      <div className="flex justify-between items-center mb-1">
        <label className="text-slate-200 font-medium">{label}</label>
        {rightElement && <div className="flex gap-2">{rightElement}</div>}
      </div>

      <div
        className={clsx(
          "w-full border rounded p-2 min-h-[44px] flex flex-wrap gap-2 items-center cursor-text bg-[#0d1117]",
          isSaving ? "border-sky-400 animate-pulse" : "border-[#2a3347]"
        )}
        onClick={() => setIsFocused(true)}
      >
        {selected.map((item) => (
          <span
            key={item}
            className="flex items-center bg-sky-900/40 text-sky-300 text-sm px-2 py-1 rounded-full"
          >
            {item}
            <button
              type="button"
              onClick={() => handleRemove(item)}
              className="ml-1 text-slate-500 hover:text-slate-300"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-grow outline-none text-sm bg-transparent text-slate-100"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
        />
      </div>
      {isFocused && filteredOptions.length > 0 && (
        <ul className="border border-[#2a3347] rounded mt-1 max-h-40 overflow-auto shadow bg-[#1e2639] z-10 absolute w-full max-w-md">
          {filteredOptions.map((opt) => (
            <li
              key={opt}
              className="px-3 py-2 hover:bg-[#2a3347] cursor-pointer text-sm text-slate-200"
              onClick={() => handleAdd(opt)}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
